import * as yup from 'yup'
import {TestContext} from 'yup'
import {RequiredStringSchema} from 'yup/lib/string'
import {AnyObject} from 'yup/lib/types'

import {Region} from '@/utils/countries'

import {ApplicantType, fullySupportedLocales, VerificationTrademarkTypeValue} from '../../constants'
import {currencies} from '../../currency'

/* Regexes */

const urlRegex =
  /^((https?|ftp):\/\/)?(www.)?(((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)?(\/((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)+(\/(([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)*)*)?)?(\?((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(#((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)|\/|\?)*)?$/i

/* Schema getters */
export const language = () => yup.string().ensure().oneOf(fullySupportedLocales).required()

export const currency = () => yup.string().ensure().oneOf(Object.values(currencies)).required()

export const country = () => yup.string().notRequired()

export const region = () => yup.string().ensure().oneOf(Object.values(Region))

export const optionalRegion = () =>
  yup
    .string()
    .ensure()
    .oneOf(['', null, undefined, ...Object.values(Region)])

export const timezone = () =>
  yup.string().test('is-valid-iana-timezone', 'Invalid IANA timezone', (value) => {
    try {
      Intl.DateTimeFormat(undefined, {timeZone: value})
      return true
    } catch {
      return false
    }
  })

export const email = (required = false) => {
  const base = yup.string().lowercase().email('error-wrong-email-format').default('')

  if (required) {
    return base.required('error-missing-email')
  }
  return base
}

export const password = () =>
  yup
    .string()
    .defined()
    .min(8, 'set-password-error-insufficient')
    .required('error-missing-password')
    .default('')

export const website = (required?: boolean) => {
  const base = yup
    .string()
    .default('https://')
    .transform(function (value: string) {
      if (value && value !== 'https://') {
        if (value.startsWith('http://') || value.startsWith('https://')) {
          return value
        } else {
          return `https://${value}`
        }
      }
      return null
    })

  if (required) {
    return base.ensure().matches(urlRegex, 'error-invalid-url').required('error-missing-website')
  }

  return base.matches(urlRegex, 'error-invalid-url').nullable().notRequired()
}

export const trademarkType = (defaultValue?: string) => {
  const base = yup
    .string()
    .required('error-missing-type')
    .oneOf([VerificationTrademarkTypeValue.text, VerificationTrademarkTypeValue.image])

  if (defaultValue) {
    return base.default(defaultValue) as RequiredStringSchema<string, AnyObject>
  }

  return base
}

export const gdpr = () =>
  yup.boolean().required().default(false).oneOf([true], 'error-missing-consent')

export const jurisdictionsValidity = (defaultValue?: any) => {
  const base = yup.array().min(1, 'error-regional-validity').required('error-regional-validity')

  if (defaultValue) {
    return base.default(defaultValue)
  }

  return base
}

export const name = (required = false) => {
  const base = yup.string().trim().ensure()

  if (required) {
    return base.required('error-missing-name').matches(/^[^0-9]+$/, 'error-missing-name')
  }
  return base
}

export const surname = (required = false) => {
  const base = yup.string().trim().ensure()

  if (required) {
    return base.required('error-missing-surname').matches(/^[^0-9]+$/, 'error-missing-surname')
  }
  return base
}

export const file = () =>
  yup.object({
    name: yup.string().required(),
    type: yup.string().required(),
    size: yup.number().required(),
    data: yup.string().optional(),
  })

/* Validators */

export const nameRequired = name(true)
export const nameOptional = name()
export const surnameRequired = surname(true)
export const surnameOptional = surname()
export const emailRequired = email(true).trim()
export const emailOptional = email().trim()
export const languageValidator = language()

/* Real utils */
export const requiredIf = <T extends yup.AnySchema>(
  schema: T,
  required: boolean,
  message: string,
): T => (required ? schema.required(message) : schema) as T

export const stringRequiredForEntity = (
  applicantTypes: ApplicantType[],
  message: string,
  pattern: RegExp,
  required = true,
) =>
  yup
    .string()
    .ensure()
    .when('type', {
      is: (type: ApplicantType) => applicantTypes.indexOf(type) !== -1,
      then: required
        ? yup.string().trim().required(message).matches(pattern, message)
        : yup.string().trim().matches(pattern, {message, excludeEmptyString: true}),
    })

export const getArrayItemTestFunctionData = (
  data: TestContext,
): {formData: any; arrayItemData: any; arrayItemIndex: number; context: any} => {
  // @ts-expect-error from is not typed
  const form = data.options.from[1].value
  // @ts-expect-error from is not typed
  const arrayItem = data.options.from[0].value

  const indexMatch = data.path.match(/\[(\d)\]/)
  const arrayItemIndex = indexMatch ? parseInt(indexMatch[1]) : 0

  // this.options.from[i].value is object on FE and JSON string on BE
  const formData = typeof form === 'string' ? JSON.parse(form) : form
  const arrayItemData = typeof arrayItem === 'string' ? JSON.parse(arrayItem) : arrayItem

  const context = data.options.context

  return {formData, arrayItemData, arrayItemIndex, context}
}

export const regionalSettingsSchema = yup.object({
  currency: currency(),
  language: language(),
  timezone: timezone().nullable().optional(),
})
